import React, { useRef, useEffect, useCallback } from 'react';
import { Popover, Modal } from 'antd';
import moment from 'moment';
import cn from 'classnames';
import Task from '../Task/Task';
import ItemToCalendarForm from '../ItemToCalendarForm/ItemToCalendarForm';
import {
  setChangeableTaskStatus,
  setLocalTask,
} from '../../store/actions/calendarActions';
import {
  useCalendarDispatch,
  useCalendarRequestInteractions,
  useCalendarState,
} from '../../context/CalendarContext';
import { containerIds } from '../../constants/containerIds';
import { modes } from '../../constants/modes';
import { views } from '../../constants/views';
import { getHeight } from '../../utils/helpers';
import { states } from '../../constants/states';
import DeleteAndEditContainer from '../DeleteAndEditContainer/DeleteAndEditContainer';
import { changeableTaskStatuses } from '../../constants/common';
import styles from './TaskWithPopover.module.scss';

const { confirm } = Modal;

const TaskWithPopover = ({
  infoDirection,
  hideWorkers,
  hideTimeInterval,
  inline,
  taskClassName,
  task,
  style,
  withShadow,
  getPopupContainer = () => document.body,
}) => {
  const dispatch = useCalendarDispatch();

  const {
    mode,
    view,
    localTask,
    state,
    changeableTaskStatus,
    innerActivationOfChange,
    showOwner,
    calendarData,
  } = useCalendarState();
  const { onTaskDelete, onTaskEditCancel, onTaskEditExit } =
    useCalendarRequestInteractions();

  const taskRef = useRef();

  const handleCancel = useCallback(() => {
    if (state === states.editTask && typeof onTaskEditCancel === 'function') {
      onTaskEditCancel();
    } else {
      // Embedded calendars own only transient grid state; they must not mutate
      // the Requests domain when their editor is closed.
      dispatch(setLocalTask(null));
    }
  }, [dispatch, onTaskEditCancel, state]);

  const handleDelete = () => {
    confirm({
      title: `Вы точно хотите удалить заявку ${localTask.requestId} из календаря?`,
      okText: 'Да',
      okType: 'danger',
      cancelText: 'Нет',
      onOk: () => {
        if (typeof onTaskDelete === 'function') {
          onTaskDelete(localTask);
        }
      },
      onCancel() {},
    });
  };

  const handleEdit = () => {
    dispatch(setChangeableTaskStatus(changeableTaskStatuses.edit));
  };

  const handleExitEditState = () => {
    dispatch(setChangeableTaskStatus(changeableTaskStatuses.edit));

    if (typeof onTaskEditExit === 'function') {
      onTaskEditExit();
    }
  };

  const isAgenda = mode === modes.agenda;
  const isMonthCalendar = mode === modes.calendar && view === views.month;

  const heightTask = showOwner && mode === modes.calendar && view === views.day;

  useEffect(() => {
    if (localTask) {
      if (taskRef.current) {
        taskRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        const hour = moment(localTask.fromDate).hour();
        const minute = moment(localTask.fromDate).minute();

        const localTaskTime = moment().set({
          hour,
          minute,
        });

        const startDayTime = moment().set({ hour: 0, minute: 0 });

        const height = getHeight(localTaskTime.diff(startDayTime, 'minutes'), heightTask, calendarData);

        window.scrollTo({
          top: height,
          behavior: 'smooth',
        });
      }
    }
  }, [localTask, taskRef]);

  return (
    <Popover
      key={changeableTaskStatus}
      getPopupContainer={getPopupContainer}
      trigger="click"
      content={
        changeableTaskStatus === changeableTaskStatuses.edit ? (
          <ItemToCalendarForm onCancel={handleCancel} />
        ) : (
          <DeleteAndEditContainer
            onDelete={handleDelete}
            onEdit={handleEdit}
            onExitEditState={
              innerActivationOfChange ? handleExitEditState : undefined
            }
          />
        )
      }
      defaultVisible
      placement="rightTop"
      overlayClassName={styles.root}
      id={containerIds.localTaskPopover}
      visible
    >
      <Task
        withPopover
        infoDirection={infoDirection}
        hideWorkers={hideWorkers}
        hideTimeInterval={hideTimeInterval}
        inline={inline}
        rootClassName={cn(styles.task, taskClassName)}
        task={task}
        style={style}
        withShadow={withShadow}
        ref={isAgenda || isMonthCalendar ? taskRef : undefined}
      />
    </Popover>
  );
};

export default TaskWithPopover;
